import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Table, TableContainer, TableHead,
  TableRow, TableCell, TableBody, Typography, Switch,
  Chip,
  Paper,
  CircularProgress
} from "@mui/material";
import RestorePageIcon from '@mui/icons-material/RestorePage';
import {
  ExpandLess,
  ExpandMore,
  Delete as DeleteIcon,
  Save as SaveIcon,
  FolderOpen as FolderOpenIcon,
  Update as UpdateIcon,
  Block as BlockIcon,
  Check as CheckIcon,
  SettingsCell,
  TableRows,
} from "@mui/icons-material";
import CustomMappingManager from "../../../components/CustomMappingManager";
import { useDynamicMappings } from "../../../hooks/useDynamicMappings";
import { useApi } from "../../../services/useApi";
import { ApiSuccessResponse } from "../../../types/api";
import { ApiResponse } from "../../../services/api.types";
export interface Template {
  id?: string;
  name?: string;
  description?: string;
  version?: string;
  mapping?: any;
  type?: 'customer' | 'supplier' | 'product' | 'finance' | 'accounting';
  defaultValues?: any;
  createdAt?: string;
  updatedAt?: string;
}

interface Props {
  mapping: any;
  defaultValues: any;
  type: 'customer' | 'supplier' | 'product' | 'finance' | 'accounting';
  onApplyMapping: (mapping: any, defaults: any, id: string) => void;
  onLoadDefaultTemplate?: () => void;
  onSuccess?: (msg: string) => void;
  onError?: (msg: string) => void;
  availableColumns?: { value: string; label: string }[];
}

const MappingDialogs = ({
  mapping,
  defaultValues, 
  type,
  onApplyMapping,
  onLoadDefaultTemplate,
  onSuccess,
  onError
}: Props) => {
  const [savedMappings, setSavedMappings] = useState<Template[]>([]);
  const [mappingName, setMappingName] = useState("");
  const [mappingDescription, setMappingDescription] = useState("");
  const [mappingVersion, setMappingVersion] = useState("1.0.0");
  const [editingMapping, setEditingMapping] = useState<Template | null>(null);
  const [selectedMappingId, setSelectedMappingId] = useState<string | null>(null);
  const [openEdit, setOpenEdit] = useState<boolean>(false);

  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);
  const [showLoadDialog, setShowLoadDialog] = useState<boolean>(false);
  const [currentMapping, setCurrentMapping] = useState<Template | null>();
  const [showMappingManager, setShowMappingManager] = useState(false);
  const [loadingUpdateTemplate, setLoadingUpdateTemplate] = useState(false);
  const {  addCustomMapping, customMappings, removeCustomMapping } = useDynamicMappings();

  const [loading, setLoading] = useState(false);
  const [loadTemplate, setLoadTemplate] = useState(false);
  const [loadingSaveTemplate, setLoadingSaveTemplate] = useState(false);
  const api = useApi();
  

  useEffect(() => {
    if(loadTemplate){
      const fetchSavedMappings = async () => {
        setLoading(true);
        try {
          const response = await api.getTemplates(type);
          setSavedMappings(response.data.data);
        } catch (error) {
          console.error("Error fetching saved mappings:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchSavedMappings();
    }

  }, [type, loadTemplate]);
  
  const handleSaveMapping = async () => {
    try {
      setLoadingSaveTemplate(true);
      if (!mappingName.trim()) return;
      const newMapping: Template = {
        name: mappingName,
        description: mappingDescription,
        version: mappingVersion,
        mapping: { ...mapping },
        defaultValues: { ...defaultValues },
        type: type
      };


      const response = await api.createTemplate(newMapping);
      
      setSavedMappings((prev) => {
        const updated = [...prev, response.data.data];
        onSuccess?.("Mapping saved successfully");
        return updated;
      });
   
    } catch (error) {
      onError?.("Error saving mapping");
    }finally{
      setMappingName("");
      setMappingDescription("");
      setMappingVersion("1.0.0");
      setShowSaveDialog(false);
      setLoadingSaveTemplate(false);
    }
    
  };

  const handleEditMapping = (m: Template) => {
    setOpenEdit(true);
    setEditingMapping(m);
    setMappingName(m.name);
    setMappingDescription(m.description);
    setMappingVersion(m.version);
  };

  const handleUpdateMapping = async () => {
    if (!editingMapping) return;
    setLoadingUpdateTemplate(true);
    const response = await api.updateTemplate(editingMapping.id, {
      name: mappingName,
      description: mappingDescription,
      version: mappingVersion,
      mapping: { ...mapping },
      defaultValues: { ...defaultValues },
      type: type
    });

    
    setSavedMappings((prev) =>
      prev.map((m) =>
        m.id === editingMapping.id
          ? {
              ...m,
              ...response.data.data
            }
          : m
      )
    );
    setEditingMapping(null);
    setOpenEdit(false);
    onSuccess?.("Template updated");
    setLoadingUpdateTemplate(false);
  };

  const handleDefaultTemplate = () => {
    onLoadDefaultTemplate();
    onSuccess?.("Default template loaded successfully");
  }

  const handleUpdateTemplate = async () => {

    const response = await api.updateTemplate(selectedMappingId, {
      mapping: { ...mapping },
      defaultValues: { ...defaultValues },
      type: type
    });
    setSavedMappings((prev) =>
    {
      const updated = prev.map((m) =>
        m.id === selectedMappingId
          ? {
              ...m,
              ...response.data.data
            }
          : m
        )
      return updated;
    }
    );
  
    onSuccess?.("Template updated");
  };

  const handleLoadMapping = (m: Template) => {
    onApplyMapping(m.mapping, m.defaultValues, m.id);
    setShowLoadDialog(false);
    setCurrentMapping(m);
    onSuccess?.("Template loaded successfully");
  };

  const handleDeleteMapping = async (id: string) => {
    const response = await api.deleteTemplate(id);
    setSavedMappings((prev) => {
      const updated = prev.filter((m) => m.id !== id);
      return updated;
    });
    onSuccess?.("Template deleted");
  };

  return (
    <>
      {/* ✅ Template Header UI */}
      <Paper elevation={3} sx={{ 
        p: 2, 
        mb: 3, 
        position: 'sticky', 
        top: 70,
        zIndex: 1000, 
        backgroundColor: 'white',
        // boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}
      
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* First line - Template info */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3,
            flexWrap: 'wrap'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" color="primary" sx={{ minWidth: '100px' }}>
                Current Template:
              </Typography>
              <Typography sx={{ fontSize: '0.875rem' }}>{currentMapping?.name || "N/A"}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>Version:</Typography>
              <Typography sx={{ fontSize: '0.875rem' }}>{currentMapping?.version || "1.0.0"}</Typography>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>Description:</Typography>
              <Typography sx={{ fontSize: '0.875rem' }}>{currentMapping?.description || "N/A"}</Typography>
            </Box>
          </Box>

          {/* Second line - Description and Actions */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3,
            flexWrap: 'wrap'
          }}>
           
            
            {/* Action buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexWrap: 'wrap'
            }}>
                            <Button
                variant="outlined"
                size="small"
                startIcon={<UpdateIcon />}
                onClick={handleUpdateTemplate}
                disabled={!selectedMappingId}
              >
                Update Current Template
              </Button>
              {/* <Button
                variant="outlined"
                size="small"
                startIcon={<RestorePageIcon />}
                onClick={handleDefaultTemplate}
              >
                Load Default
              </Button> */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<FolderOpenIcon />}
                onClick={() => {
                  setLoadTemplate(true);
                  setShowLoadDialog(true);
                }}
              >
                Templates
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SaveIcon />}
                onClick={() => setShowSaveDialog(true)}
                disabled={!mapping}
              >
                Save New
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TableRows />}
                onClick={() => setShowMappingManager(true)}
              >
                Manage Cell Mappings
              </Button>

            </Box>
          </Box>
        </Box>
      </Paper>
      {/* Save Dialog */}
      <Dialog open={showSaveDialog} maxWidth="sm"
        fullWidth>
        <DialogTitle>Save Template</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                     <TextField
                       autoFocus
                       label="Name"
                       fullWidth
                       value={mappingName}
                       onChange={(e) => setMappingName(e.target.value)}
                     />
                     <TextField
                       label="Description"
                       fullWidth
                       multiline
                       rows={2}
                       value={mappingDescription}
                       onChange={(e) => setMappingDescription(e.target.value)}
                     />
                     <TextField
                       label="Version"
                       fullWidth
                       value={mappingVersion}
                       onChange={(e) => setMappingVersion(e.target.value)}
                     />
                   </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>{setShowSaveDialog(false)}}>Cancel</Button>
          <Button onClick={handleSaveMapping} disabled={loadingSaveTemplate}>{loadingSaveTemplate ? <CircularProgress size={24} /> : "Save"}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEdit} maxWidth="sm"
        fullWidth>
        <DialogTitle>Edit Template</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField autoFocus fullWidth label="Name" value={mappingName} onChange={(e) => setMappingName(e.target.value)} />
            <TextField fullWidth
              multiline rows={2}
              label="Description" value={mappingDescription} onChange={(e) => setMappingDescription(e.target.value)} />
            <TextField fullWidth label="Version" value={mappingVersion} onChange={(e) => setMappingVersion(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>{setOpenEdit(false)}}>Cancel</Button>
          <Button onClick={handleUpdateMapping} disabled={loadingUpdateTemplate}>{loadingUpdateTemplate ? <CircularProgress size={24} /> : "Update"}</Button>
        </DialogActions>
      </Dialog>

      {/* Load Dialog */}
      <Dialog open={showLoadDialog} maxWidth="md" fullWidth>
        <DialogTitle>Load Template</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? <TableRow><TableCell align="center" colSpan={5}><CircularProgress /></TableCell></TableRow> : savedMappings.length > 0 ? savedMappings.map((m) => (
                  <TableRow key={m.id} selected={currentMapping?.id === m.id} onClick={() => setSelectedMappingId(m.id)}>
                    <TableCell>{m.name}</TableCell>
                    <TableCell>{m.description}</TableCell>
                    <TableCell>{m.version}</TableCell>
                    <TableCell>
                    <Chip
                        label={currentMapping?.id === m.id ? "Active" : "Inactive"}
                        color={currentMapping?.id === m.id ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                       <Box sx={{ display: "flex", gap: 1 }}>
                       <Button      
                          variant="contained" 
                          startIcon={<FolderOpenIcon />}
                          size="small" onClick={() => handleLoadMapping(m)}>Load</Button>
                      <Button  variant="outlined" startIcon={<UpdateIcon />}
                          size="small" onClick={() => handleEditMapping(m)}>Edit</Button>
                      <Button  color="error" size="small" onClick={() => handleDeleteMapping(m.id)}><DeleteIcon /></Button>
                       </Box>
                      
                    </TableCell>
                  </TableRow>
                )) : <TableRow><TableCell align="center" colSpan={5}>No templates found</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setShowLoadDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <CustomMappingManager
        open={showMappingManager}
        onClose={() => setShowMappingManager(false)}
        onSave={addCustomMapping}
        onDelete={(mapping) => removeCustomMapping(mapping.id)}
        existingMappings={customMappings}
      />
    </>
  );
};

export default MappingDialogs;
